import { useMutation, useQueryClient, type UseMutationOptions } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';

interface NotificationMutationOptions<TData, TError extends Error, TVariables, TContext>
  extends Omit<UseMutationOptions<TData, TError, TVariables, TContext>, 'onSuccess' | 'onError'> {
  /** Message shown on success. If a function, receives (data, variables). Set to false to suppress. */
  successMessage?: string | ((data: TData, variables: TVariables) => string) | false;
  /** Message shown on error. Defaults to error.message. Set to false to suppress. */
  errorMessage?: string | ((error: TError) => string) | false;
  /** Query keys to invalidate on success */
  invalidateKeys?: unknown[][];
  /** Original onSuccess — runs after notification */
  onSuccess?: (data: TData, variables: TVariables, context: TContext | undefined) => void;
  /** Original onError — runs after notification */
  onError?: (error: TError, variables: TVariables, context: TContext | undefined) => void;
}

export function useMutationWithNotification<
  TData = unknown,
  TError extends Error = Error,
  TVariables = void,
  TContext = unknown,
>(options: NotificationMutationOptions<TData, TError, TVariables, TContext>) {
  const queryClient = useQueryClient();
  const { successMessage, errorMessage, invalidateKeys, onSuccess, onError, ...rest } = options;

  return useMutation<TData, TError, TVariables, TContext>({
    ...rest,
    onSuccess: (data, variables, context) => {
      if (invalidateKeys) {
        for (const key of invalidateKeys) {
          queryClient.invalidateQueries({ queryKey: key });
        }
      }

      if (successMessage !== false) {
        const message = typeof successMessage === 'function'
          ? successMessage(data, variables)
          : successMessage || 'Operation completed successfully';
        notifications.show({ title: 'Success', message, color: 'teal' });
      }

      onSuccess?.(data, variables, context);
    },
    onError: (error, variables, context) => {
      if (errorMessage !== false) {
        const message = typeof errorMessage === 'function'
          ? errorMessage(error)
          : errorMessage || error.message || 'An error occurred';
        notifications.show({ title: 'Error', message, color: 'red' });
      }

      onError?.(error, variables, context);
    },
  });
}
